import { useState, useEffect, useRef } from 'react';
import { patientsApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Users, Loader2, Search, Upload, FileDown } from 'lucide-react';
import { toast } from 'sonner';

export default function Patients() {
  const { isAdmin } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    phone_alt: '',
    email: '',
    date_of_birth: '',
    fiscal_code: '',
    address: '',
    pathologies: '',
    medications: '',
    notes: ''
  });

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async (search = '') => {
    try {
      setLoading(true);
      const response = await patientsApi.list(search);
      setPatients(response.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPatients(searchQuery);
  };

  const openDialog = (patient = null) => {
    if (patient) {
      setEditingPatient(patient);
      const nameParts = (patient.name || '').split(' ');
      setFormData({
        first_name: patient.first_name || nameParts[0] || '',
        last_name: patient.last_name || nameParts.slice(1).join(' ') || '',
        phone: patient.phone,
        phone_alt: patient.phone_alt || '',
        email: patient.email || '',
        date_of_birth: patient.date_of_birth || '',
        fiscal_code: patient.fiscal_code || '',
        address: patient.address || '',
        pathologies: patient.pathologies || '',
        medications: patient.medications || '',
        notes: patient.notes || ''
      });
    } else {
      setEditingPatient(null);
      setFormData({
        first_name: '',
        last_name: '',
        phone: '',
        phone_alt: '',
        email: '',
        date_of_birth: '',
        fiscal_code: '',
        address: '',
        pathologies: '',
        medications: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = { ...formData };
      // Combine first_name + last_name into name for backward compatibility
      data.name = `${data.first_name} ${data.last_name}`.trim();
      if (!data.email) delete data.email;
      if (!data.date_of_birth) delete data.date_of_birth;
      if (!data.fiscal_code) delete data.fiscal_code;
      if (!data.address) delete data.address;
      if (!data.phone_alt) data.phone_alt = "";

      if (editingPatient) {
        await patientsApi.update(editingPatient.id, data);
        toast.success('Paziente aggiornato');
      } else {
        await patientsApi.create(data);
        toast.success('Paziente creato');
      }
      
      setDialogOpen(false);
      loadPatients(searchQuery);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (patient) => {
    if (!window.confirm(`Eliminare il paziente "${patient.name}"?`)) return;
    
    try {
      await patientsApi.delete(patient.id);
      toast.success('Paziente eliminato');
      loadPatients(searchQuery);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await patientsApi.uploadCsv(file);
      const { imported, total_errors, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`${imported} pazienti importati con successo`);
      }
      if (total_errors > 0) {
        toast.warning(`${total_errors} righe con errori`);
        console.log('Import errors:', errors);
      }
      
      setUploadDialogOpen(false);
      loadPatients();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    const csv = 'name,phone,email,date_of_birth,fiscal_code,address,notes\nMario Rossi,+39 333 1234567,mario@esempio.it,1980-01-15,RSSMRA80A15H501A,Via Roma 1,Note esempio';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_pazienti.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <PageHeader 
        title="Pazienti" 
        subtitle="Gestisci l'anagrafica dei pazienti"
        actions={
          <div className="flex gap-3">
            {isAdmin && (
              <Button 
                variant="outline"
                onClick={() => setUploadDialogOpen(true)}
                data-testid="upload-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importa CSV
              </Button>
            )}
            <Button 
              onClick={() => openDialog()} 
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="add-patient-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Paziente
            </Button>
          </div>
        }
      />

      <div className="content-area" data-testid="patients-list">
        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6 flex gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca per nome, telefono o codice fiscale..."
              className="pl-10"
              data-testid="patient-search-input"
            />
          </div>
          <Button type="submit" variant="outline" data-testid="patient-search-btn">
            Cerca
          </Button>
        </form>

        <div className="table-container">
          <Table>
            <TableHeader className="table-header">
              <TableRow>
                <TableHead>Paziente</TableHead>
                <TableHead>Telefono</TableHead>
                <TableHead className="hidden lg:table-cell">Tel. Alt.</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Cod. Fiscale</TableHead>
                <TableHead>Data Nascita</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : patients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    {searchQuery ? 'Nessun paziente trovato' : 'Nessun paziente registrato'}
                  </TableCell>
                </TableRow>
              ) : (
                patients.map((patient) => (
                  <TableRow key={patient.id} data-testid={`patient-row-${patient.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-violet-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-violet-600">
                            {patient.name.charAt(0)}
                          </span>
                        </div>
                        <span className="font-medium text-slate-800">{patient.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{patient.phone}</TableCell>
                    <TableCell className="text-slate-600 hidden lg:table-cell">{patient.phone_alt || '-'}</TableCell>
                    <TableCell className="text-slate-600">{patient.email || '-'}</TableCell>
                    <TableCell className="text-slate-600">{patient.fiscal_code || '-'}</TableCell>
                    <TableCell className="text-slate-600">{patient.date_of_birth || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDialog(patient)}
                        data-testid={`edit-patient-${patient.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(patient)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-patient-${patient.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="patient-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingPatient ? 'Modifica Paziente' : 'Nuovo Paziente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Nome *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Mario"
                  required
                  className="mt-1.5"
                  data-testid="patient-first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Cognome *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Rossi"
                  required
                  className="mt-1.5"
                  data-testid="patient-last-name-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Telefono *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                  required
                  className="mt-1.5"
                  data-testid="patient-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="phone_alt">Tel. Alternativo</Label>
                <Input
                  id="phone_alt"
                  value={formData.phone_alt}
                  onChange={(e) => setFormData({ ...formData, phone_alt: e.target.value })}
                  placeholder="+39 06 1234567"
                  className="mt-1.5"
                  data-testid="patient-phone-alt-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@esempio.it"
                  className="mt-1.5"
                  data-testid="patient-email-input"
                />
              </div>
              <div>
                <Label htmlFor="date_of_birth">Data di Nascita</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="mt-1.5"
                  data-testid="patient-dob-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fiscal_code">Codice Fiscale</Label>
                <Input
                  id="fiscal_code"
                  value={formData.fiscal_code}
                  onChange={(e) => setFormData({ ...formData, fiscal_code: e.target.value.toUpperCase() })}
                  placeholder="RSSMRA80A15H501A"
                  className="mt-1.5"
                  maxLength={16}
                  data-testid="patient-cf-input"
                />
              </div>
              <div>
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Via Roma 1, Milano"
                  className="mt-1.5"
                  data-testid="patient-address-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pathologies">Patologie / Terapie in corso</Label>
              <Textarea
                id="pathologies"
                value={formData.pathologies}
                onChange={(e) => setFormData({ ...formData, pathologies: e.target.value })}
                placeholder="Es: Ipertensione, Diabete tipo 2..."
                className="mt-1.5"
                rows={2}
                data-testid="patient-pathologies-input"
              />
            </div>

            <div>
              <Label htmlFor="medications">Medicinali ricorrenti</Label>
              <Textarea
                id="medications"
                value={formData.medications}
                onChange={(e) => setFormData({ ...formData, medications: e.target.value })}
                placeholder="Es: Metformina 500mg, Ramipril 5mg..."
                className="mt-1.5"
                rows={2}
                data-testid="patient-medications-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Note</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                className="mt-1.5"
                rows={2}
                data-testid="patient-notes-input"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="patient-submit-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingPatient ? 'Aggiorna' : 'Crea Paziente'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload CSV Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Importa Pazienti da CSV
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-sm text-slate-600">
              Carica un file CSV con i dati dei pazienti. Le colonne supportate sono:
              <code className="block mt-2 p-2 bg-slate-100 rounded text-xs">
                name, phone, email, date_of_birth, fiscal_code, address, notes
              </code>
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="w-full"
                data-testid="download-template-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Scarica Template CSV
              </Button>
              
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full bg-sky-500 hover:bg-sky-600"
                  data-testid="select-file-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importazione...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Seleziona File CSV
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
